import moment from 'moment-timezone';
import ValueFilter from './ValueFilter';

export const PresetPeriod = {
  SIX_WEEKS: '6W',
  THREE_MONTHS: '3M',
  SIX_MONTHS: '6M',
  ONE_YEAR: '1Y',
  CUSTOM: 'custom',
} as const;

export type PresetPeriodType = (typeof PresetPeriod)[keyof typeof PresetPeriod];

export const availablePeriods: Record<
  Exclude<PresetPeriodType, 'custom'>,
  { value: number; unit: moment.unitOfTime.DurationConstructor }
> = {
  [PresetPeriod.SIX_WEEKS]: { value: 6, unit: 'weeks' },
  [PresetPeriod.THREE_MONTHS]: { value: 3, unit: 'months' },
  [PresetPeriod.SIX_MONTHS]: { value: 6, unit: 'months' },
  [PresetPeriod.ONE_YEAR]: { value: 1, unit: 'years' },
};

interface DateRangeFilterValue {
  start: string;
  end: string;
}

class DateRangeFilter extends ValueFilter<DateRangeFilterValue> {
  zone?: string;
  forecastPeriodEnd: boolean;

  constructor(value: DateRangeFilterValue, forecastPeriodEnd: boolean = true) {
    super(value);

    this.forecastPeriodEnd = forecastPeriodEnd;

    if (window.currentUser?.zone) {
      this.zone = window.currentUser.zone;
    }
  }

  toBookmarkConfiguration() {
    return this.getValue();
  }

  set(value: DateRangeFilterValue) {
    super.set(value ?? this.calculatePeriod(PresetPeriod.SIX_WEEKS));
  }

  setPresetPeriod(period: PresetPeriodType) {
    this.set(this.calculatePeriod(period ?? PresetPeriod.SIX_WEEKS));
  }

  getTzMoment(value?: string) {
    // Calling moment(undefined) is the same as moment()
    if (this.zone) {
      return moment(value).tz(this.zone);
    }

    return moment(value);
  }

  calculatePeriod(period: PresetPeriodType): DateRangeFilterValue {
    const end = this.presetEndTime();

    const periodData =
      availablePeriods[period] || availablePeriods[PresetPeriod.SIX_WEEKS];

    const start = end
      .clone()
      .subtract(periodData.value, periodData.unit)
      .toISOString();

    return {
      start,
      end: end.toISOString(),
    };
  }

  get presetPeriod() {
    for (let period of Object.values(PresetPeriod)) {
      const data = availablePeriods[period];
      if (!data) continue;

      if (
        // Only consider a preset "active" if it's the same range that using one of the buttons would have given.
        // (i.e. 2 dates 1 year apart are not necessarily same as selecting the 1Y button)
        this.endTime.isSame(this.presetEndTime(), 'day') &&
        this.endTime.diff(this.startTime, data.unit, true) === data.value
      ) {
        return period;
      }
    }

    return PresetPeriod.CUSTOM;
  }

  get startTime() {
    const value = this.getValue();
    return this.getTzMoment(value.start);
  }

  get endTime() {
    const value = this.getValue();
    return this.getTzMoment(value.end);
  }

  presetEndTime() {
    if (this.forecastPeriodEnd)
      return this.getTzMoment().add(1, 'weeks').startOf('week');

    return this.getTzMoment().add(1, 'day').startOf('day');
  }
}

export default DateRangeFilter;
